import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { TbGripVertical, TbTrash } from 'react-icons/tb';
import { CSSObject } from '@emotion/react';
import { theme } from 'styles';
import { mobile } from 'styles/media';
import { IContentsModule } from 'types';
import { MODULE_ITEM_TYPE, MODULE_META } from './constants';

const { color, radius } = theme;

const item = (isDragging: boolean): CSSObject => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  height: 44,
  padding: '0 4px 0 6px',
  borderRadius: radius.md,
  opacity: isDragging ? 0.4 : 1,
  cursor: 'grab',
  transition: 'background-color 0.15s ease',
  ':hover': { background: color.grey50 },
  ':hover > button': { opacity: 1 },
  '> svg': { flex: 'none', color: color.grey300 },
});

const label: CSSObject = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 14,
  color: color.text,
  '> svg': { flex: 'none', color: color.grey500 },
  '> span': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

const removeButton: CSSObject = {
  flex: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  border: 'none',
  borderRadius: radius.sm,
  background: 'transparent',
  color: color.grey400,
  opacity: 0,
  cursor: 'pointer',
  ':hover': { background: color.errorBg, color: color.error },
  ':focus-visible': { opacity: 1 },
  [mobile]: { opacity: 1 },
};

interface DragItem {
  id: number;
  index: number;
}

interface Props {
  module: IContentsModule;
  index: number;
  handleMove: (from: number, to: number) => void;
  handleRemove: (id: number) => void;
}

function ModuleManageItem({ module, index, handleMove, handleRemove }: Props) {
  const ref = useRef<HTMLLIElement>(null);
  const meta = MODULE_META[module.type];

  const [, drop] = useDrop<DragItem>({
    accept: MODULE_ITEM_TYPE,
    hover(dragItem, monitor) {
      if (!ref.current || dragItem.index === index) return;

      const rect = ref.current.getBoundingClientRect();
      const offset = monitor.getClientOffset();
      if (!offset) return;
      const hoverY = offset.y - rect.top;
      const middleY = rect.height / 2;

      if (dragItem.index < index && hoverY < middleY) return;
      if (dragItem.index > index && hoverY > middleY) return;

      handleMove(dragItem.index, index);
      dragItem.index = index;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: MODULE_ITEM_TYPE,
    item: (): DragItem => ({ id: module.id, index }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  drag(drop(ref));

  return (
    <li ref={ref} css={item(isDragging)}>
      <TbGripVertical size={16} />
      <div css={label}>
        {meta?.icon}
        <span>{meta?.label ?? module.type}</span>
      </div>
      <button
        type="button"
        css={removeButton}
        onClick={() => handleRemove(module.id)}
        aria-label={`${meta?.label ?? ''} 삭제`}
      >
        <TbTrash size={16} />
      </button>
    </li>
  );
}

export default ModuleManageItem;
